
import { parseScript } from "~/transform-script-engine"
import { formatTransformScriptReference } from "~/transform-script/reference"
import { evaluateTransformScriptOffline } from "~/transform-script/evaluator-legacy"

import { Component, Prop, Watch } from "vue-property-decorator"
import BaseInput from "~/core/BaseInput"
import { v4 as uuidv4 } from "uuid"

import types from "./types"

interface FunctionParam {
  label: string
  type: string
  value?: string
  useRef?: boolean
}

interface FunctionItem {
  name: string
  color: string
  params?: FunctionParam[]
  id?: string
  enableDefault?: boolean
  default?: string
  defaultUseRef: boolean
}

interface TransformFunctionCommand {
  cmd: string
  description: string
}

interface TransformFunctionDisplayCommand extends TransformFunctionCommand {
  group: string
}

interface TransformFunctionGroup {
  group: string
  commands: TransformFunctionCommand[]
}

interface ArrtibuteMappingItem {
  value: string
  label: string
  type: string
}

@Component
export default class TransformScript extends BaseInput {
  @Prop({ type: Array, default: () => [] }) readonly keys!: string[]
  @Prop({ type: Array, default: () => [] }) readonly staticKeys!: string[]
  @Prop(Boolean) readonly disabled!: boolean
  @Prop({ type: String, default: "" }) readonly currentFieldKey!: string
  @Prop({ type: String, default: "" }) readonly currentFieldLabel!: string
  @Prop({ type: String, default: "" }) readonly currentFieldDefaultValue!: string
  codeView = false
  mockView = false
  hasSortedAvailableFunctions = false
  showAddFunctionDialog = false
  selectedTransformFunctionGroupIndex = 0
  transformCommandSearch = ""

  showDialog = false
  script = ""
  mockValues: Record<string, string> = {}
  previewPending = false
  previewError = ""
  previewOutput = ""
  previewRenamedField = ""
  previewUnsupportedFunctions: string[] = []
  previewTimer: ReturnType<typeof setTimeout> | null = null
  functions: FunctionItem[] = []
  functionItemSelectedIndex = -1
  functionItemSelected: FunctionItem = {
    name: "",
    color: "",
    defaultUseRef: false,
  }

  transformFunctionGroups: TransformFunctionGroup[] = [
    {
      group: "Text Cleanup",
      commands: [
        { cmd: "trim_space", description: "Remove leading and trailing whitespace." },
        { cmd: "lowercase", description: "Convert the current value to lowercase." },
        { cmd: "uppercase", description: "Convert the current value to uppercase." },
        { cmd: "collapse_space", description: "Trim and collapse repeated whitespace to one space." },
        { cmd: "keep_digits", description: "Keep only digits 0-9 and remove all other characters." },
        { cmd: "remove_prefix", description: "Remove a prefix when the current value starts with it." },
        { cmd: "remove_suffix", description: "Remove a suffix when the current value ends with it." },
      ],
    },
    {
      group: "Text Compose",
      commands: [
        { cmd: "concat", description: "Append another value to the current value." },
        { cmd: "prefix_with", description: "Add text before the current value." },
        { cmd: "subfix_with", description: "Add text after the current value." },
        { cmd: "replace_string", description: "Find text and replace it with another value." },
        { cmd: "repeat_string", description: "Repeat the current value a fixed number of times." },
        { cmd: "split_string", description: "Split text by a separator and select a position." },
        { cmd: "explode_by_pipe", description: "Split pipe-separated text and select a position." },
      ],
    },
    {
      group: "Value & Mapping",
      commands: [
        { cmd: "overwrite_value", description: "Replace the current value with a fixed value or reference." },
        { cmd: "build_column", description: "Create or rename an output column with a specific value." },
        { cmd: "default_if_empty", description: "Use a default value when the trimmed input is empty." },
        { cmd: "replace_if_not_number", description: "Replace empty or invalid numeric input with a value." },
        { cmd: "map_value", description: "Map source values to target values using from/to pairs." },
        { cmd: "autonumber", description: "Generate a running number with an optional prefix." },
      ],
    },
    {
      group: "Math",
      commands: [
        { cmd: "add", description: "Add a number to the current numeric value." },
        { cmd: "subtract", description: "Subtract a number from the current numeric value." },
        { cmd: "multiply", description: "Multiply the current numeric value by a number." },
        { cmd: "divide", description: "Divide the current numeric value by a number." },
        { cmd: "round", description: "Round the current numeric value to decimal places." },
        { cmd: "ceil", description: "Round up to the nearest integer." },
        { cmd: "floor", description: "Round down to the nearest integer." },
        { cmd: "format_decimal", description: "Format the number with fixed decimal places." },
        { cmd: "clamp", description: "Limit the number to a min and max range." },
        { cmd: "sum_fields", description: "Sum multiple referenced numeric fields." },
        { cmd: "math_expressions", description: "Calculate a value with a math expression." },
      ],
    },
    {
      group: "Data Extract",
      commands: [
        { cmd: "read_from_json", description: "Read a nested value from JSON text." },
        { cmd: "read_from_array", description: "Read a value from an array-like input." },
      ],
    },
    {
      group: "Date & Time",
      commands: [
        { cmd: "to_system_datetime", description: "Parse inbound datetime and normalize it to PAM system format." },
        { cmd: "from_system_datetime", description: "Format PAM system datetime for an outbound destination." },
        { cmd: "datetime", description: "Alias of to_system_datetime. Kept for existing scripts." },
        { cmd: "format_date_string", description: "Alias of from_system_datetime. Kept for existing scripts." },
        { cmd: "convert_timezone", description: "Convert datetime values between timezone offsets." },
        { cmd: "datetime_to_unix_timestamp", description: "Convert datetime text to a Unix timestamp." },
        { cmd: "unix_timestamp_to_datetime", description: "Convert a Unix timestamp to datetime text." },
      ],
    },
    {
      group: "Encoding & Security",
      commands: [
        { cmd: "decode", description: "Decode base64, base64url, URL, or hex text." },
        { cmd: "hash", description: "Hash the current value with the selected algorithm." },
        { cmd: "hex_utf16", description: "Convert hex UTF-16 text to readable text." },
        { cmd: "binary_uuid_to_string", description: "Convert binary UUID data to string format." },
        { cmd: "generate_jwt", description: "Generate a JWT with issuer and hex secret." },
      ],
    },
    {
      group: "Storage & External",
      commands: [
        { cmd: "base64_to_s3", description: "Upload base64 content to S3 and return the stored path." },
        { cmd: "encrypt_to_s3", description: "Encrypt content and store it in S3." },
        { cmd: "s3_to_decrypt", description: "Read encrypted S3 content and decrypt it." },
        { cmd: "s3_to_base64", description: "Read S3 content and return base64 text." },
        { cmd: "convert_redirect_id_to_contact_id", description: "Resolve a redirect ID into a contact ID." },
      ],
    },
    {
      group: "Phone & Control",
      commands: [
        { cmd: "add_region_phone_number", description: "Add a region code to a phone number." },
        { cmd: "exit_if_equal", description: "Stop the pipeline with a fallback value when values match." },
      ],
    },
  ]

  get arrtibutesKeyList() {
    let r: ArrtibuteMappingItem[] = []
    this.staticKeys.forEach((t) => {
      const key = t.trim()
      r.push({
        value: key,
        label: key,
        type: "static",
      })
    })

    this.keys.forEach((t) => {
      const key = t.trim()
      r.push({
        value: key,
        label: key,
        type: "attribute",
      })
    })

    return r
  }

  get previewFieldKey(): string {
    return (this.currentFieldKey || this.currentFieldLabel || this.name || "_self").trim()
  }

  get previewFieldLabel(): string {
    return this.currentFieldLabel || this.previewFieldKey
  }

  get mockFieldKeys(): string[] {
    const keys = new Set<string>()
    if (this.previewFieldKey) {
      keys.add(this.previewFieldKey)
    }
    this.keys.forEach((key) => key && keys.add(key.trim()))
    this.staticKeys.forEach((key) => key && keys.add(key.trim()))
    return Array.from(keys)
  }

  get currentMockInput(): string {
    return this.mockValues[this.previewFieldKey] ?? ""
  }

  get mockStorageKey(): string {
    return `transform-script-preview:${this.name || this.previewFieldKey}`
  }

  get previewApproxFunctions(): string[] {
    const approx = new Set([
      "autonumber",
      "base64_to_s3",
      "encrypt_to_s3",
      "s3_to_decrypt",
      "s3_to_base64",
      "generate_jwt",
      "convert_redirect_id_to_contact_id",
    ])
    return this.functions
      .map((item) => item.name)
      .filter((name, index, list) => approx.has(name) && list.indexOf(name) === index)
  }

  get selectedTransformFunctionGroup(): TransformFunctionGroup {
    return this.transformFunctionGroups[this.selectedTransformFunctionGroupIndex] || this.transformFunctionGroups[0]
  }

  get isSearchingTransformCommand(): boolean {
    return (this.transformCommandSearch || "").trim().length > 0
  }

  get displayedTransformCommands(): TransformFunctionDisplayCommand[] {
    const search = (this.transformCommandSearch || "").trim().toLowerCase()
    if (!search) {
      return (this.selectedTransformFunctionGroup?.commands || []).map((command) => ({
        ...command,
        group: this.selectedTransformFunctionGroup.group,
      }))
    }

    return this.transformFunctionGroups
      .flatMap((group) =>
        group.commands.map((command) => ({
          ...command,
          group: group.group,
          commandRank: command.cmd.toLowerCase().includes(search) ? 0 : 1,
        }))
      )
      .filter(
        (command) =>
          command.commandRank === 0 ||
          command.description.toLowerCase().includes(search) ||
          command.group.toLowerCase().includes(search)
      )
      .sort((a, b) => a.commandRank - b.commandRank || a.cmd.localeCompare(b.cmd))
      .map(({ commandRank, ...command }) => command)
  }

  availableFunctions: FunctionItem[] = [
    {
      name: "add_region_phone_number",
      color: "#F2EAC4",
      enableDefault: false,
      default: "",
      defaultUseRef: false,
      params: [{ label: "Region", type: "string", useRef: false, value: "" }],
    },
    {
      name: "build_column",
      color: "#d6d445",
      enableDefault: false,
      default: "",
      defaultUseRef: false,
      params: [
        {
          label: "Column Name",
          type: "string",
          useRef: false,
          value: "",
        },
        {
          label: "Column Value",
          type: "string",
          useRef: false,
          value: "",
        },
      ],
    },
    {
      name: "autonumber",
      color: "#95DBDA",
      enableDefault: false,
      default: "",
      defaultUseRef: false,
      params: [{ label: "Prefix", type: "string", useRef: false, value: "" }],
    },
    {
      name: "base64_to_s3",
      color: "#d6d445",
      enableDefault: false,
      default: "",
      defaultUseRef: false,
      params: [{ label: "Path", type: "string", useRef: false, value: "" },
      { label: "File name prefix", type: "string", useRef: false, value: "" },
      { label: "Storage mode (public/private)", type: "string", useRef: false, value: "private" }
      ],
    },
    {
      name: "binary_uuid_to_string",
      color: "#9BAADD",
      enableDefault: false,
      default: "",
      defaultUseRef: false,
      params: [],
    },
    {
      name: "encrypt_to_s3",
      color: "#d6d445",
      enableDefault: false,
      default: "",
      defaultUseRef: false,
      params: [{ label: "Path", type: "string", useRef: false, value: "" },
      { label: "File name prefix", type: "string", useRef: false, value: "" }
      ],
    },
    {
      name: "s3_to_decrypt",
      color: "#d6d445",
      enableDefault: false,
      default: "",
      defaultUseRef: false,
      params: [],
    },
    {
      name: "s3_to_base64",
      color: "#d6d445",
      enableDefault: false,
      default: "",
      defaultUseRef: false,
      params: [
        { label: "Storage mode (public/private)", type: "string", useRef: false, value: "private" },
      ],
    },
    {
      name: "concat",
      color: "#9AE3B7",
      enableDefault: false,
      default: "",
      defaultUseRef: false,
      params: [
        { label: "Concat with", type: "string", useRef: false, value: "" },
        {
          label: "Seperate With Space",
          type: "boolean",
          useRef: false,
          value: "false",
        },
      ],
    },
    {
      name: "decode",
      color: "#9BAADD",
      enableDefault: false,
      default: "",
      defaultUseRef: false,
      params: [
        {
          label: "Method (base64/base64url/url/hex)",
          type: "string",
          useRef: false,
          value: "base64",
        },
      ],
    },
    {
      name: "convert_timezone",
      color: "#d9771c",
      enableDefault: false,
      default: "",
      defaultUseRef: false,
      params: [
        {
          label: "Source Offset",
          type: "int",
          useRef: false,
          value: "0",
        },
        {
          label: "Destination Offset",
          type: "int",
          useRef: false,
          value: "0",
        },
      ],
    },
    {
      name: "convert_redirect_id_to_contact_id",
      color: "#d9771c",
      enableDefault: false,
      default: "",
      defaultUseRef: false,
      params: [],
    },
    {
      name: "datetime",
      color: "#FD7BED",
      enableDefault: false,
      default: "",
      defaultUseRef: false,
      params: [
        {
          label: "DateTime layout",
          type: "string",
          useRef: false,
          value: "yyyy-MM-dd HH:mm:ss",
        },
      ],
    },
    {
      name: "to_system_datetime",
      color: "#FD7BED",
      enableDefault: false,
      default: "",
      defaultUseRef: false,
      params: [
        {
          label: "Source DateTime layout",
          type: "string",
          useRef: false,
          value: "yyyy-MM-dd HH:mm:ss",
        },
      ],
    },
    {
      name: "datetime_to_unix_timestamp",
      color: "#d9771c",
      enableDefault: false,
      default: "",
      defaultUseRef: false,
      params: [
        {
          label: "Input Offset",
          type: "int",
          useRef: false,
          value: "0",
        },
      ],
    },
    {
      name: "exit_if_equal",
      color: "#ff758c",
      enableDefault: false,
      default: "",
      defaultUseRef: false,
      params: [
        { label: "Compare With", type: "string", useRef: false, value: "" },
        {
          label: "Fallback Value",
          type: "string",
          useRef: false,
          value: "",
        },
      ],
    },
    {
      name: "explode_by_pipe",
      color: "#E6CDA3",
      enableDefault: false,
      default: "",
      defaultUseRef: false,
      params: [
        { label: "Select position", type: "int", useRef: false, value: "0" },
      ],
    },
    {
      name: "format_date_string",
      color: "#FD7BED",
      enableDefault: false,
      default: "",
      defaultUseRef: false,
      params: [
        {
          label: "Format Date layout",
          type: "string",
          useRef: false,
          value: "yyyy-MM-ddTHH:mm:ss.000000Z",
        },
      ],
    },
    {
      name: "from_system_datetime",
      color: "#FD7BED",
      enableDefault: false,
      default: "",
      defaultUseRef: false,
      params: [
        {
          label: "Output DateTime layout",
          type: "string",
          useRef: false,
          value: "yyyy-MM-ddTHH:mm:ss.000000Z",
        },
      ],
    },
    {
      name: "hash",
      color: "#9BAADD",
      enableDefault: false,
      default: "",
      defaultUseRef: false,
      params: [
        {
          label: "Algorithm",
          type: "string",
          useRef: false,
          value: "",
        },
      ],
    },
    {
      name: "generate_jwt",
      color: "#9BAADD",
      enableDefault: false,
      default: "",
      defaultUseRef: false,
      params: [
        { label: "Issuer", type: "string", useRef: false, value: "" },
        { label: "Secret (hex)", type: "string", useRef: false, value: "" },
      ],
    },
    {
      name: "hex_utf16",
      color: "#9BAADD",
      enableDefault: false,
      default: "",
      defaultUseRef: false,
      params: [],
    },
    {
      name: "math_expressions",
      color: "#4DC0BF",
      enableDefault: false,
      default: "",
      defaultUseRef: false,
      params: [
        {
          label: "Math expressions",
          type: "string",
          useRef: false,
          value: "",
        },
      ],
    },
    {
      name: "overwrite_value",
      color: "#CA9E54",
      enableDefault: false,
      default: "",
      defaultUseRef: false,
      params: [{ label: "Value", type: "string", useRef: false, value: "" }],
    },
    {
      name: "prefix_with",
      color: "#F2EAC4",
      enableDefault: false,
      default: "",
      defaultUseRef: false,
      params: [{ label: "Prefix", type: "string", useRef: false, value: "" }],
    },
    {
      name: "read_from_array",
      color: "#4DC0BF",
      enableDefault: false,
      default: "",
      defaultUseRef: false,
      params: [
        {
          label: "Read from array",
          type: "string",
          useRef: false,
          value: "",
        },
      ],
    },
    {
      name: "read_from_json",
      color: "#4DC0BF",
      enableDefault: false,
      default: "",
      defaultUseRef: false,
      params: [
        {
          label: "Read from json",
          type: "string",
          useRef: false,
          value: "",
        },
      ],
    },
    {
      name: "repeat_string",
      color: "#F0A8A8",
      enableDefault: false,
      default: "",
      defaultUseRef: false,
      params: [
        { label: "Repeat times", type: "int", useRef: false, value: "0" },
      ],
    },
    {
      name: "replace_string",
      color: "#349A89",
      enableDefault: false,
      default: "",
      defaultUseRef: false,
      params: [
        { label: "Find", type: "string", useRef: false, value: "" },
        { label: "Replace With", type: "string", useRef: false, value: "" },
      ],
    },
    {
      name: "split_string",
      color: "#EAB0E3",
      enableDefault: false,
      default: "",
      defaultUseRef: false,
      params: [
        { label: "Seperator", type: "string", useRef: false, value: "" },
        { label: "Select position", type: "int", useRef: false, value: "0" },
      ],
    },
    {
      name: "subfix_with",
      color: "#95DBDA",
      enableDefault: false,
      default: "",
      defaultUseRef: false,
      params: [{ label: "Subfix", type: "string", useRef: false, value: "" }],
    },
    {
      name: "trim_space",
      color: "#9BAADD",
      enableDefault: false,
      default: "",
      defaultUseRef: false,
      params: [],
    },
    {
      name: "lowercase",
      color: "#9BAADD",
      enableDefault: false,
      default: "",
      defaultUseRef: false,
      params: [],
    },
    {
      name: "uppercase",
      color: "#9BAADD",
      enableDefault: false,
      default: "",
      defaultUseRef: false,
      params: [],
    },
    {
      name: "collapse_space",
      color: "#9BAADD",
      enableDefault: false,
      default: "",
      defaultUseRef: false,
      params: [],
    },
    {
      name: "keep_digits",
      color: "#9BAADD",
      enableDefault: false,
      default: "",
      defaultUseRef: false,
      params: [],
    },
    {
      name: "remove_prefix",
      color: "#F2EAC4",
      enableDefault: false,
      default: "",
      defaultUseRef: false,
      params: [{ label: "Prefix", type: "string", useRef: false, value: "" }],
    },
    {
      name: "remove_suffix",
      color: "#95DBDA",
      enableDefault: false,
      default: "",
      defaultUseRef: false,
      params: [{ label: "Suffix", type: "string", useRef: false, value: "" }],
    },
    {
      name: "default_if_empty",
      color: "#ff758c",
      enableDefault: false,
      default: "",
      defaultUseRef: false,
      params: [{ label: "Default Value", type: "string", useRef: false, value: "" }],
    },
    {
      name: "replace_if_not_number",
      color: "#ff758c",
      enableDefault: false,
      default: "",
      defaultUseRef: false,
      params: [{ label: "Replacement Value", type: "string", useRef: false, value: "" }],
    },
    {
      name: "map_value",
      color: "#d6d445",
      enableDefault: false,
      default: "",
      defaultUseRef: false,
      params: [
        { label: "From 1", type: "string", useRef: false, value: "" },
        { label: "To 1", type: "string", useRef: false, value: "" },
      ],
    },
    {
      name: "add",
      color: "#4DC0BF",
      enableDefault: false,
      default: "",
      defaultUseRef: false,
      params: [{ label: "Number", type: "float", useRef: false, value: "" }],
    },
    {
      name: "subtract",
      color: "#4DC0BF",
      enableDefault: false,
      default: "",
      defaultUseRef: false,
      params: [{ label: "Number", type: "float", useRef: false, value: "" }],
    },
    {
      name: "multiply",
      color: "#4DC0BF",
      enableDefault: false,
      default: "",
      defaultUseRef: false,
      params: [{ label: "Number", type: "float", useRef: false, value: "" }],
    },
    {
      name: "divide",
      color: "#4DC0BF",
      enableDefault: false,
      default: "",
      defaultUseRef: false,
      params: [{ label: "Number", type: "float", useRef: false, value: "" }],
    },
    {
      name: "round",
      color: "#4DC0BF",
      enableDefault: false,
      default: "",
      defaultUseRef: false,
      params: [{ label: "Decimal Places", type: "int", useRef: false, value: "0" }],
    },
    {
      name: "ceil",
      color: "#4DC0BF",
      enableDefault: false,
      default: "",
      defaultUseRef: false,
      params: [],
    },
    {
      name: "floor",
      color: "#4DC0BF",
      enableDefault: false,
      default: "",
      defaultUseRef: false,
      params: [],
    },
    {
      name: "format_decimal",
      color: "#4DC0BF",
      enableDefault: false,
      default: "",
      defaultUseRef: false,
      params: [{ label: "Decimal Places", type: "int", useRef: false, value: "0" }],
    },
    {
      name: "clamp",
      color: "#4DC0BF",
      enableDefault: false,
      default: "",
      defaultUseRef: false,
      params: [
        { label: "Min", type: "float", useRef: false, value: "" },
        { label: "Max", type: "float", useRef: false, value: "" },
      ],
    },
    {
      name: "sum_fields",
      color: "#4DC0BF",
      enableDefault: false,
      default: "",
      defaultUseRef: false,
      params: [
        { label: "Field 1", type: "string", useRef: true, value: "" },
        { label: "Field 2", type: "string", useRef: true, value: "" },
      ],
    },
    {
      name: "unix_timestamp_to_datetime",
      color: "#d9771c",
      enableDefault: false,
      default: "",
      defaultUseRef: false,
      params: [],
    },
  ]

  itemClass(index: number) {
    return {
      active: this.functionItemSelectedIndex === index,
    }
  }

  removeStatrment(statementID?: string) {
    if (!statementID) {
      return
    }
    const removedIndex = this.functions.findIndex((t) => t.id === statementID)
    const selectedId = this.functionItemSelected?.id
    this.functions = this.functions.filter((t) => t.id !== statementID)
    if (this.functions.length === 0) {
      this.functionItemSelectedIndex = -1
      this.functionItemSelected = {
        name: "",
        color: "",
        defaultUseRef: false,
      }
      return
    }
    const selectedIndex = this.functions.findIndex((t) => t.id === selectedId)
    const nextIndex =
      selectedIndex >= 0
        ? selectedIndex
        : removedIndex >= 0
          ? Math.min(removedIndex, this.functions.length - 1)
          : Math.min(this.functionItemSelectedIndex, this.functions.length - 1)
    this.selectItem(nextIndex)
  }

  moveFunction(index: number, direction: -1 | 1) {
    const target = index + direction
    if (target < 0 || target >= this.functions.length) {
      return
    }
    const next = [...this.functions]
    const [item] = next.splice(index, 1)
    if (!item) {
      return
    }
    next.splice(target, 0, item)
    this.functions = next
    this.selectItem(target)
  }

  renderFunctionPreview(item: FunctionItem) {
    return `${item.name}${this.paramBracket(item)}`
  }

  addMapValuePair() {
    if (!this.functionItemSelected.params) {
      return
    }
    const pairIndex = Math.floor(this.functionItemSelected.params.length / 2) + 1
    this.functionItemSelected.params.push(
      { label: `From ${pairIndex}`, type: "string", useRef: false, value: "" },
      { label: `To ${pairIndex}`, type: "string", useRef: false, value: "" }
    )
  }

  addSumFieldParam() {
    if (!this.functionItemSelected.params) {
      return
    }
    const fieldIndex = this.functionItemSelected.params.length + 1
    this.functionItemSelected.params.push({
      label: `Field ${fieldIndex}`,
      type: "string",
      useRef: true,
      value: "",
    })
  }

  get code() {
    let code = ""
    for (let i in this.functions) {
      code += `${this.functions[i].name}${this.paramBracket(this.functions[i])}`

      if (Number(i) < this.functions.length - 1) {
        code += " |\n"
      }
    }

    return code
  }

  @Watch("showDialog")
  onShowDialog(newValue: boolean) {
    if (newValue) {
      if (!this.hasSortedAvailableFunctions) {
        this.orderFunctions()
        this.hasSortedAvailableFunctions = true
      }
      this.renderScriptString()
      this.loadMockValues()
      this.runPreview()
    } else {
      this.mockView = false
      this.codeView = false
      this.showAddFunctionDialog = false
    }
  }

  @Watch("code")
  onCodeChange() {
    this.queuePreview()
  }

  @Watch("mockFieldKeys")
  onMockFieldKeysChange() {
    this.ensureMockValueKeys()
    this.queuePreview()
  }

  resetDefaultValue() {
    if (this.functionItemSelected) {
      this.functionItemSelected!.default = ""
    }
  }

  toggleMockView() {
    this.mockView = !this.mockView
    if (this.mockView) {
      this.codeView = false
      this.ensureMockValueKeys()
      this.runPreview()
    }
  }

  toggleCodeView() {
    this.mockView = false
    this.codeView = !this.codeView
  }

  loadMockValues() {
    let values: Record<string, string> = {}
    if (typeof window !== "undefined") {
      try {
        values = JSON.parse(window.localStorage.getItem(this.mockStorageKey) || "{}")
      } catch {
        values = {}
      }
    }
    this.mockValues = values
    this.ensureMockValueKeys()
  }

  ensureMockValueKeys() {
    this.mockFieldKeys.forEach((key) => {
      if (this.mockValues[key] === undefined) {
        this.$set(
          this.mockValues,
          key,
          key === this.previewFieldKey ? String(this.currentFieldDefaultValue || "") : ""
        )
      }
    })
  }

  persistMockValues() {
    if (typeof window === "undefined") {
      return
    }
    window.localStorage.setItem(this.mockStorageKey, JSON.stringify(this.mockValues))
  }

  onMockValueInput() {
    this.persistMockValues()
    this.queuePreview()
  }

  queuePreview() {
    if (!this.showDialog || !this.mockView) {
      return
    }
    if (this.previewTimer) {
      clearTimeout(this.previewTimer)
    }
    this.previewTimer = setTimeout(() => {
      this.runPreview()
    }, 200)
  }

  async runPreview() {
    this.previewPending = true
    this.previewError = ""
    this.previewOutput = ""
    this.previewRenamedField = ""
    this.previewUnsupportedFunctions = []

    try {
      if (!this.code.trim()) {
        return
      }

      const result = await evaluateTransformScriptOffline(this.code, {
        input: this.currentMockInput,
        references: this.mockValues,
      })

      this.previewOutput = result.output
      this.previewRenamedField = result.renamedField
      this.previewUnsupportedFunctions = result.unsupportedFunctions
    } catch (error) {
      this.previewError = error instanceof Error ? error.message : String(error)
    } finally {
      this.previewPending = false
    }
  }

  selectItem(index: number) {
    this.functionItemSelected = this.functions[index]
    this.functionItemSelectedIndex = index
  }

  isFloat(n: number) {
    return Number(n) === n && n % 1 !== 0
  }

  shouldAppendDefaultValue(func: FunctionItem): boolean {
    return func.enableDefault === true
  }

  paramBracket(func: FunctionItem) {
    const params = func.params ?? []
    let p: string[] = []
    for (let i in params) {
      const t = params[i]

      if (t.value != undefined) {
        if (t.useRef) {
          p.push(formatTransformScriptReference(t.value))
        } else if (t.type == "string") {
          const escaped = t.value.replaceAll(`"`, `\\"`)
          p.push(`"${escaped}"`)
        } else if (t.type == "boolean") {
          let r = t.value
          if (t.value !== "true" && t.value !== "false") {
            r = "false"
          }
          p.push(r)
        } else {
          let num = Number(t.value)
          let r = ""
          if (isNaN(num)) {
            num = 0
          }

          if (t.type === "int") {
            if (this.isFloat(num)) {
              r = String(Math.floor(num))
            } else {
              r = num.toString()
            }
          }

          if (t.type === "float") {
            if (!this.isFloat(num)) {
              r = `${num}.0`
            } else {
              r = t.value
            }
          }

          p.push(r)
        }
      }
      // else {
      //   p += `<${t.type}>`;
      // }
    }

    let result = `(${p.join(", ")})`

    if (this.shouldAppendDefaultValue(func)) {
      const defaultVal = func.default ?? ""
      if (func.defaultUseRef && defaultVal.trim().length > 0) {
        result += `?:${formatTransformScriptReference(defaultVal)}`
      } else {
        result += `?:"${defaultVal}"`
      }
    }
    return result
  }

  addFunction(func: FunctionItem) {
    let item = JSON.parse(JSON.stringify(func))
    item.id = uuidv4()
    this.functions.push(item)
    this.showAddFunctionDialog = false
  }

  clickDiscard() {
    this.showDialog = false
    this.script = this.value
  }

  clickSave() {
    this.showDialog = false
    this.script = this.code
    this.onInput(this.code)
  }

  openEditor() {
    if (this.disabled) {
      return
    }
    this.showDialog = true
  }

  openAddFunctionDialog() {
    this.selectedTransformFunctionGroupIndex = Math.min(
      this.selectedTransformFunctionGroupIndex,
      this.transformFunctionGroups.length - 1
    )
    this.transformCommandSearch = ""
    this.showAddFunctionDialog = true
  }

  openTransformScriptDocs() {
    const lang =
      typeof window !== "undefined" && window.navigator.language?.toLowerCase().startsWith("th")
        ? "th"
        : "en"
    const docsUrl = `/marketing/docs?collection=program-documentation&lang=${lang}&doc=transform-script`

    window.open(docsUrl, "_blank", "noopener")
  }

  addFunctionByName(name: string) {
    const func = this.availableFunctions.find((item) => item.name === name)
    if (!func) {
      return
    }
    this.addFunction(func)
  }

  renderScriptString() {
    const statements = parseScript(this.value)
    let functionsStack: FunctionItem[] = []

    for (let i = 0; i < statements.length; i++) {
      const stmt = statements[i]

      let fnTemplate = this.availableFunctions.filter((t) => {
        return t.name === stmt.functionName
      })

      if (fnTemplate.length == 1) {
        let fn: FunctionItem = JSON.parse(JSON.stringify(fnTemplate[0]))
        fn.id = uuidv4()

        if (stmt.defaultValue && stmt.defaultValue.type) {
          fn.defaultUseRef = stmt.defaultValue?.type === "reference"
          fn.enableDefault = true
          fn.default = stmt.defaultValue?.value
        }

        for (let k = 0; k < stmt.param.length; k++) {
          let p = stmt.param[k]
          if (!fn.params![k]) {
            fn.params!.push({
              label: fn.name === "map_value"
                ? `${k % 2 === 0 ? "From" : "To"} ${Math.floor(k / 2) + 1}`
                : `Field ${k + 1}`,
              type: "string",
              useRef: false,
              value: "",
            })
          }
          fn.params![k].useRef = p.type === "reference"
          fn.params![k].value = p.value
        }
        functionsStack.push(fn)
      }
    }

    this.functionItemSelected = {
      name: "",
      color: "",
      defaultUseRef: false,
    }
    this.functions = functionsStack
    this.script = this.value
  }

  orderFunctions() {
    //Order function name from alphabet
    this.availableFunctions = this.availableFunctions.sort((a, b) => {
      if (a.name > b.name) {
        return 1
      } else if (a.name < b.name) {
        return -1
      }
      return 0
    })
  }

}
